import React from 'react'
import { useTranslation } from 'react-i18next'
import { Box, DataListItem } from '@node-space/ui-components'
import { SidePanelSection } from 'components/SidePanel/SidePanelSection'
import type { CardTransaction } from 'services/CardService'
import { truncateId } from 'utils/string'
import { useCardTransactionDetails } from '../hooks/useCardTransactionDetails'

interface CardTransactionDetailsContentProps {
  transaction: CardTransaction
}

export const CardTransactionDetailsContent = ({
  transaction,
}: CardTransactionDetailsContentProps) => {
  const { t } = useTranslation()

  const { conversionRate, localAmount, authorizedAmount, settledAmount, fee, merchantItems } =
    useCardTransactionDetails(transaction)

  return (
    <Box flexCol gap={20} width="full" paddingT={16}>
      <SidePanelSection heading={t('customers.cardTransactionDetails.transactionDetails')}>
        <Box flexCol width="full">
          <DataListItem
            id="transactionId"
            title={t('customers.cardTransactionDetails.transactionId')}
            body={truncateId(transaction.id)}
            copyText={transaction.id}
            layout="horizontal"
          />
          {localAmount && (
            <DataListItem
              id="localAmountPaid"
              title={t('customers.cardTransactionDetails.localAmountPaid')}
              body={localAmount}
              layout="horizontal"
            />
          )}
          {fee && (
            <DataListItem
              id="fee"
              title={t('customers.cardTransactionDetails.fee')}
              body={fee}
              layout="horizontal"
            />
          )}
          {conversionRate && (
            <DataListItem
              id="conversionRate"
              title={t('customers.cardTransactionDetails.conversionRate')}
              body={conversionRate}
              layout="horizontal"
            />
          )}
          <DataListItem
            id="authorizedAmount"
            title={t('customers.cardTransactionDetails.authorizedAmount')}
            body={authorizedAmount}
            layout="horizontal"
          />
          {settledAmount && (
            <DataListItem
              id="settledAmount"
              title={t('customers.cardTransactionDetails.settledAmountFromWallet')}
              body={settledAmount}
              layout="horizontal"
            />
          )}
        </Box>
      </SidePanelSection>

      {merchantItems.length > 0 && (
        <SidePanelSection heading={t('customers.cardTransactionDetails.merchantInformation')}>
          <Box flexCol width="full">
            {merchantItems.map(({ id, value }) => (
              <DataListItem
                key={id}
                id={id}
                title={t(`customers.cardTransactionDetails.${id}`)}
                body={value}
                layout="horizontal"
              />
            ))}
          </Box>
        </SidePanelSection>
      )}
    </Box>
  )
}
